/**
 * @friskai/frisk-js Frisk SDK
 *
 * Main entry point for the Frisk SDK. Provides a high-level API for
 * policy evaluation, session management, and tool wrapping.
 */

import {
  FRISK_API_KEY,
  FRISK_BASE_URL,
  FRISK_BASE_URL_DEFAULT,
  FRISK_TELEMETRY_ENDPOINT,
  FRISK_TELEMETRY_ENDPOINT_DEFAULT,
  getEnv,
} from '../env'
import {
  FriskNotInitializedError,
  MissingAPIKeyError,
  MissingBaseURLError,
  MissingOtlpEndpointError,
  MissingToolPoliciesExtensionError,
} from '../errors'
import { BaseFrameworkAdapter, FrameworkAdapter } from '../framework-adapter'
import {
  DefaultWrapToolOptions,
  WrapToolOptions,
} from '../framework-adapter/framework-adapter'
import { LogLevel, SDKLogger } from '../logging'
import { deriveSdkLogger } from '../logging/derive-sdk-logger'
import { TRACER_NAME, TRACER_VERSION } from '../telemetry'
import { TracingManager } from '../telemetry/tracing-manager'
import { redactObject } from '../utils/redact'
import { AccessTokenProvider } from './access-token-provider'
import type {
  DecideToolCallOptions,
  FriskExtension,
  ToolPoliciesProvider,
} from './extensions'
import { FriskSession } from './frisk-session'
import {
  type ResolvedRedactionOptions,
  resolveRedactionOptions,
} from './redaction-options'
import {
  getCoreSdkAttributes,
  SdkAttributes,
} from './sdk-attributes/get-core-sdk-attributes'
import { SessionRegistry } from './session-registry'
import {
  cancelManyToolApprovalRequests,
  getOrCreateManyToolApprovalRequests,
  getOrCreateToolApprovalRequest,
  type ToolApprovalRequest,
  type ToolApprovalRequestInput,
  type ToolApprovalRequestStatusUpdateResult,
} from './tool-approval-request'
import { ToolRegistry, ToolRegistryItem } from './tool-registry'
import {
  FriskOptions,
  NormalizedToolCall,
  ToolCall,
  ToolCallDecision,
} from './types'

type Ctor<T> = new (opts: any) => T

/**
 * Main Frisk SDK class
 *
 * Provides a high-level API for:
 * - Policy evaluation for tool calls
 * - Session management for tracking conversations
 * - Redaction of sensitive data
 * - Integration with LangChain and other frameworks
 *
 * @example
 * ```typescript
 * import { Frisk } from '@friskai/frisk-js';
 *
 * const frisk = new Frisk({
 *   apiKey: process.env.FRISK_API_KEY,
 *   baseUrl: 'https://api.frisk.ai',
 *   redact: {
 *     redactToolArgs: ['password', 'api_key'],
 *     redactAgentState: true,
 *   },
 * });
 *
 * // Create a session for tracking
 * const sessionId = frisk.createSession();
 *
 * // Evaluate a tool call
 * const result = frisk.decideToolCall(
 *   'send_email',
 *   { recipient: 'user@example.com' },
 *   { userId: 123 }
 * );
 *
 * if (result.outcome === DecisionOutcome.ALLOW) {
 *   // Execute the tool
 * }
 * ```
 */
export class Frisk<
  ToolCallT extends ToolCall = ToolCall,
  ToolT = unknown,
  FrameworkAdapterT extends FrameworkAdapter<
    ToolCallT,
    ToolT
  > = BaseFrameworkAdapter,
  SessionMetadataT = never,
> {
  get toolPoliciesAvailable(): boolean {
    return this.toolPolicies?.available ?? false
  }
  get redaction(): ResolvedRedactionOptions {
    return this._redaction
  }
  private _redaction: ResolvedRedactionOptions
  public readonly apiBaseUrl: string
  public readonly otlpEndpoint: string
  public readonly accessTokenProvider: AccessTokenProvider

  get adapter(): FrameworkAdapterT {
    return this._adapter
  }
  get logLevel(): LogLevel | undefined {
    return this._logLevel
  }
  get tracingManager(): TracingManager {
    if (!this._tracingManager) {
      throw new FriskNotInitializedError()
    }
    return this._tracingManager
  }
  protected initialized: boolean = false
  protected rootLogger: SDKLogger
  protected logger: SDKLogger
  private _logLevel?: LogLevel
  private _adapter!: FrameworkAdapterT
  private _tracingManager: TracingManager | null = null
  private toolRegistry: ToolRegistry
  private sdkAttributes: SdkAttributes
  private extensions: FriskExtension[] = []
  private toolPolicies: ToolPoliciesProvider | null = null

  /*
   * Create and initialize a Frisk instance.
   */
  private wrapToolsCalled: boolean = false
  public get toolRegistrationComplete(): Promise<boolean> {
    if (!this.wrapToolsCalled) {
      return Promise.resolve(true)
    }

    return this.toolRegistry.initialized
  }

  public static async connect<T extends Frisk>(
    this: Ctor<T>,
    options?: FriskOptions,
  ): Promise<T> {
    const instance = new this(options)
    await instance.connect()

    return instance
  }

  constructor(options?: FriskOptions<ToolCallT, ToolT, FrameworkAdapterT>) {
    this.rootLogger = deriveSdkLogger(options?.logging, {
      sdk: 'frisk',
    })
    this._logLevel = options?.logging?.logLevel
    this.logger = deriveSdkLogger(
      {
        logger: this.rootLogger,
        logLevel: this.logLevel,
      },
      {
        component: 'FriskCore',
      },
    )

    // Set framework adapter (defaults to BaseFrameworkAdapter)
    this._adapter =
      options?.adapter ?? (new BaseFrameworkAdapter() as FrameworkAdapterT)

    this.sdkAttributes = this.getSdkAttributes()

    // Resolve API key
    const apiKey = options?.apiKey ?? getEnv(FRISK_API_KEY)
    if (!apiKey) {
      throw new MissingAPIKeyError()
    }

    // Resolve base URL
    const baseUrl =
      options?.apiBaseUrl ?? getEnv(FRISK_BASE_URL, FRISK_BASE_URL_DEFAULT)
    if (!baseUrl) {
      throw new MissingBaseURLError()
    }
    this.apiBaseUrl = baseUrl

    this.accessTokenProvider = AccessTokenProvider.getInstance({
      apiKey,
      baseUrl,
      logging: {
        logger: this.rootLogger,
        logLevel: this.logLevel,
      },
    })
    this.toolRegistry = new ToolRegistry({
      apiBaseUrl: baseUrl,
      getAccessToken: async () => this.accessTokenProvider.getAccessToken(),
      logger: this.logger,
    })

    // Resolve telemetry endpoint
    const otlpEndpoint =
      options?.otlpEndpoint ??
      getEnv(FRISK_TELEMETRY_ENDPOINT, FRISK_TELEMETRY_ENDPOINT_DEFAULT)

    if (!otlpEndpoint) {
      throw new MissingOtlpEndpointError()
    }
    this.otlpEndpoint = otlpEndpoint

    // Normalize redaction options
    this._redaction = resolveRedactionOptions(options?.redact)
    this.extensions = options?.extensions ?? []
    this.mountExtensions()
  }

  private mountExtensions(): void {
    for (const ext of this.extensions) {
      if ('available' in ext && 'decideToolCall' in ext) {
        this.toolPolicies = ext as ToolPoliciesProvider
      }
    }
  }

  private async initializeExtensions(): Promise<void> {
    for (const ext of this.extensions) {
      await ext.initialize(this)
    }
  }

  public async connect(): Promise<void> {
    if (!this.initialized) {
      this.logger.debug?.('Initializing Frisk SDK...')
      this.logger.debug?.(`Connecting to Frisk API at ${this.apiBaseUrl}`)
      this.logger.debug?.(`Sending telemetry to: ${this.otlpEndpoint}`)
      const currentToken = await this.accessTokenProvider.getAccessToken()
      // Pass a token provider function that returns the current token
      // This allows the OTEL SDK to use the latest token on each export
      this._tracingManager = new TracingManager({
        telemetryEndpoint: this.otlpEndpoint,
        authToken: currentToken,
        tracerName: TRACER_NAME,
        tracerVersion: TRACER_VERSION,
      })

      this.accessTokenProvider.addCallback((newToken) =>
        this.updateAccessToken(newToken),
      )
      this.accessTokenProvider.startBackgroundRefresh()
      this.initialized = true

      await this.initializeExtensions()
    }
  }

  /**
   * Create a new session for tracking tool calls
   *
   * @returns The session ID
   */
  session(
    metadata?: SessionMetadataT,
  ): FriskSession<
    Frisk<ToolCallT, ToolT, FrameworkAdapterT, SessionMetadataT>
  > {
    const session = new FriskSession<
      Frisk<ToolCallT, ToolT, FrameworkAdapterT, SessionMetadataT>
    >({
      frisk: this,
      redact: this.redaction,
      tracer: this.tracingManager.getTracer(), // Initialized in init()
      logging: {
        logger: this.rootLogger,
        logLevel: this.logLevel,
      },
    })

    // Register session in the global registry
    const registry = SessionRegistry.getInstance()
    registry.register(session.id, session)

    return session
  }

  /**
   * Extract tool call information using the framework adapter
   */
  normalizeToolCall(toolCall: ToolCallT): NormalizedToolCall {
    return this.adapter.normalizeToolCall(toolCall)
  }

  /**
   * Wrap tools with LLM reasoning.
   */
  wrapTools(
    tools: Iterable<ToolT>,
    options: WrapToolOptions = DefaultWrapToolOptions,
  ): ToolT[] {
    this.wrapToolsCalled = true
    const toolList = [...tools]
    const registerToolProperties = toolList
      .map((tool) => this.adapter.extractRegisterToolProperties?.(tool))
      .filter(
        (properties): properties is NonNullable<typeof properties> =>
          properties !== null && properties !== undefined,
      )
    if (registerToolProperties.length > 0) {
      this.toolRegistry.registerTools({
        tools: registerToolProperties,
        sdkAttributes: this.sdkAttributes,
      })
    }
    if (this.adapter.wrapTools) {
      return this.adapter.wrapTools(toolList, options)
    }
    return toolList
  }

  private getSdkAttributes(): SdkAttributes {
    return {
      ...getCoreSdkAttributes(),
      agentFramework: this.adapter.agentFramework ?? null,
      agentFrameworkVersion: this.adapter.agentFrameworkVersion ?? null,
    }
  }

  /**
   * Wrap a single tool with LLM reasoning using the framework adapter
   */
  wrapTool(
    tool: ToolT,
    options: WrapToolOptions = DefaultWrapToolOptions,
  ): ToolT {
    if (this.adapter.wrapTool) {
      return this.adapter.wrapTool(tool, options)
    }
    return tool
  }

  /**
   * Get a session by ID
   */
  getSessionFromRegistry(sessionId: string): FriskSession {
    const registry = SessionRegistry.getInstance()
    return registry.get(sessionId)
  }

  /**
   * Update the authentication token
   *
   * This is used for token refresh when the current token expires.
   */
  updateAccessToken(authToken: string): void {
    this.toolPolicies?.updateAccessToken(authToken)
    this.tracingManager.updateAuthToken(authToken)
  }

  getRegisteredTool(toolName: string): ToolRegistryItem | null {
    return this.toolRegistry.getRegisteredTool(toolName)
  }

  /**
   * Evaluate a tool call against policies and return a BLOCK or ALLOW decision.
   *
   * @param toolName - Name of the tool being called
   * @param toolArgs - Arguments passed to the tool
   * @param agentState - Current state of the agent
   * @param toolCallId - Optional unique identifier for the tool call
   * @param traceContextCarrier - Optional trace context for distributed tracing
   * @returns The tool call decision
   */
  decideToolCall({
    toolCall,
    agentState,
    traceContextCarrier,
  }: DecideToolCallOptions): ToolCallDecision {
    if (!this.toolPolicies) {
      throw new MissingToolPoliciesExtensionError()
    }

    const toolArgsJson = toolCall.args ? JSON.stringify(toolCall.args) : null
    const agentStateJson = agentState ? JSON.stringify(agentState) : null

    const redactedArgs = toolCall.args
      ? redactObject(
          toolCall.args as Record<string, unknown>,
          this.redaction.redactToolArgs,
        )
      : { value: {}, redactedPaths: [] as string[] }
    const toolArgsRedactedJson = toolCall.args
      ? JSON.stringify(redactedArgs.value)
      : null
    const toolArgsRedactedPathsJson = JSON.stringify(redactedArgs.redactedPaths)

    const redactedState = agentState
      ? redactObject(
          agentState as Record<string, unknown>,
          this.redaction.redactAgentState,
        )
      : { value: {}, redactedPaths: [] as string[] }
    const agentStateRedactedJson = agentState
      ? JSON.stringify(redactedState.value)
      : null
    const agentStateRedactedPathsJson = JSON.stringify(
      redactedState.redactedPaths,
    )

    return this.toolPolicies.decideToolCall({
      toolCall,
      toolArgsJson,
      toolArgsRedactedJson,
      toolArgsRedactedPathsJson,
      agentStateJson,
      agentStateRedactedJson,
      agentStateRedactedPathsJson,
      traceContextCarrier,
    })
  }

  async getOrCreateToolApprovalRequest({
    toolCallId,
    toolName,
    toolId,
    toolVersionId,
    policyId,
    policyVersionId,
    sessionId,
  }: {
    toolCallId: string
    toolName: string
    toolId?: string | null
    toolVersionId?: string | null
    policyId: string
    policyVersionId: string
    sessionId: string
  }): Promise<ToolApprovalRequest | null> {
    return getOrCreateToolApprovalRequest({
      toolCallId,
      toolName,
      toolId,
      toolVersionId,
      policyId,
      policyVersionId,
      sessionId,
      apiBaseUrl: this.apiBaseUrl,
      accessToken: await this.accessTokenProvider.getAccessToken(),
    })
  }

  async cancelManyToolApprovalRequests({
    approvalRequestIds,
  }: {
    approvalRequestIds: string[]
  }): Promise<Record<string, ToolApprovalRequestStatusUpdateResult | null>> {
    return cancelManyToolApprovalRequests({
      approvalRequestIds,
      apiBaseUrl: this.apiBaseUrl,
      accessToken: await this.accessTokenProvider.getAccessToken(),
    })
  }

  async getOrCreateManyToolApprovalRequests({
    toolApprovalRequestInputs,
    sessionId,
  }: {
    toolApprovalRequestInputs: Iterable<ToolApprovalRequestInput>
    sessionId: string
  }): Promise<Record<string, ToolApprovalRequest | null>> {
    return getOrCreateManyToolApprovalRequests({
      toolApprovalRequestInputs,
      sessionId,
      apiBaseUrl: this.apiBaseUrl,
      accessToken: await this.accessTokenProvider.getAccessToken(),
    })
  }

  /**
   * Shutdown the Frisk SDK and flush telemetry
   *
   * This method flushes all pending spans to the telemetry backend
   * before shutting down. Always call this before your process exits
   * to ensure all telemetry data is exported.
   */
  async shutdown(): Promise<void> {
    this.logger.info?.('Frisk is shutting down...')

    // Flush and shutdown the OpenTelemetry SDK first to export pending spans
    if (this.tracingManager) {
      await this.tracingManager.shutdown()
    }

    for (const ext of this.extensions) {
      ext.shutdown()
    }
  }
}
